import SpineWrapper from '@Core/Animation/SpineWrapper';
import { ISubLevel, IWinLevel } from '@GFTypes/GameStoreTypes';
import { EWinType } from '@Managers/Redux/HttpState';
import WinSoundsManager from '@Managers/SoundManager/WinSoundsManager';
import { GameScene, IWinConfiguration } from '..';
import { EWinPresentationStates } from './States/WinPresentationStates';

export class BaseWinLevelsConfigParser {
    private readonly accumulatedLevelNames: EWinType[] = [];

    constructor(
        private readonly winLevelsConfig: IWinLevel[],
        private readonly winConfig: IWinConfiguration,
        private readonly winSoundManager: WinSoundsManager,
        private readonly scene: GameScene,
        private readonly winSpine: SpineWrapper,
        private readonly accumulateSubLevels: boolean = false
    ) {
        this.winLevelsConfig = this.prepareWinLevelsConfig(winLevelsConfig);
    }

    public getFinalWinLevel(bet: number, totalWin: number): IWinLevel | undefined {
        return this.winLevelsConfig.find((level) => level.to && level.to * bet > totalWin);
    }

    public getSubLevel(winLevel: IWinLevel, winLevelName?: string): { current?: ISubLevel; next?: ISubLevel } {
        if (!winLevel.subLevels) return {};
        if (!winLevelName) return { current: winLevel.subLevels[0], next: winLevel.subLevels[1] };

        const sublevelIndex = winLevel.subLevels.findIndex((s) => s.name === winLevelName);
        return { current: winLevel.subLevels[sublevelIndex], next: winLevel.subLevels[sublevelIndex + 1] };
    }

    public getWinLevels(): IWinLevel[] {
        return this.winLevelsConfig;
    }

    public getValueToStartWinLevel(bet: number, winLevel: number | EWinType): number {
        let from: number | undefined;

        if (typeof winLevel === 'number') {
            from = this.winLevelsConfig[winLevel]?.from;
        } else {
            const foundLevel = this.winLevelsConfig.find((lvl) => lvl.name === winLevel);
            from = foundLevel?.from;
        }

        if (from === undefined) {
            console.warn(`[WinLevels] Could not find win level for`, winLevel);
            return 0;
        }

        return bet * from;
    }

    public getOdometerDurationsPerWinLevel(winLevelsConfig: IWinLevel[]): number[] {
        return winLevelsConfig.map((level) => this.calculateTotalOdometerDuration(level));
    }

    private prepareWinLevelsConfig(winLevelsConfig: IWinLevel[]): IWinLevel[] {
        return winLevelsConfig.toSorted((a, b) => a.from - b.from).map((level, index) => this.prepareLevel(level, index));
    }

    private prepareLevel(level: IWinLevel, index: number): IWinLevel {
        const odometerDuration = this.getOdometerDuration(level, index);
        const odometerHoldDuration = this.getOdometerHoldDuration(level);

        const expectedLevels = this.getExpectedLevels(level);

        const subLevels = this.buildSubLevels(level, expectedLevels, odometerDuration);

        this.trackAccumulatedLevel(level);

        return {
            ...level,
            odometerDuration,
            odometerHoldDuration,
            subLevels: subLevels.length ? subLevels : undefined
        };
    }

    private getOdometerDuration(level: IWinLevel, index: number): number {
        return level.odometerDuration ?? this.winSoundManager.getWinLevelSoundDuration(index);
    }

    private getOdometerHoldDuration(level: IWinLevel): number | undefined {
        return level.odometerHoldDuration ?? this.winConfig.odometer?.holdDuration;
    }

    private getExpectedLevels(level: IWinLevel): EWinType[] {
        const levelName = level.name ?? EWinType.NORMAL_WIN;
        if (!this.accumulateSubLevels)
            return levelName === EWinType.NORMAL_WIN ? [EWinType.NORMAL_WIN] : level.subLevels!.map((sublevel) => sublevel.name as EWinType);
        const baseLevels = this.accumulatedLevelNames.length > 0 ? [...this.accumulatedLevelNames] : [];
        if (!baseLevels.includes(EWinType.NORMAL_WIN)) baseLevels.unshift(EWinType.NORMAL_WIN);
        return [...new Set<EWinType>([...baseLevels, levelName])];
    }

    private buildSubLevels(level: IWinLevel, expectedLevels: EWinType[], totalOdometerDuration: number): ISubLevel[] {
        const definedSubLevels = level.subLevels ?? [];
        const definedTotalDuration = definedSubLevels.reduce((sum, sl) => sum + sl.odometerDuration, 0);

        const missingLevels = expectedLevels.filter((name) => !definedSubLevels.some((sl) => sl.name === name));
        const remainingTime = Math.max(totalOdometerDuration - definedTotalDuration, 0);
        const distributedDuration = missingLevels.length > 0 ? remainingTime / missingLevels.length : 0;

        return expectedLevels.map((name) => {
            const existingLevel = definedSubLevels.find((sl) => sl.name === name);
            const from = this.winLevelsConfig.find((level) => level.name === name)?.from ?? 0;
            return {
                name,
                from,
                odometerDuration: existingLevel?.odometerDuration ?? distributedDuration,
                particleEmitters: existingLevel?.particleEmitters ?? level.particleEmitters,
                odometerFontKey: existingLevel?.odometerFontKey ?? level.odometerFontKey ?? this.winConfig.odometer!.fontKey,
                odometerFontSize: existingLevel?.odometerFontSize ?? level.odometerFontSize ?? this.winConfig.odometer!.fontSize,
                duration: existingLevel?.duration
            };
        });
    }

    private trackAccumulatedLevel(level: IWinLevel) {
        const levelName = level.name ?? EWinType.NORMAL_WIN;
        if (!this.accumulatedLevelNames.includes(levelName)) {
            this.accumulatedLevelNames.push(levelName);
        }
    }

    private calculateTotalOdometerDuration(level: IWinLevel): number {
        const { odometerDuration, subLevels } = level;
        if (!subLevels) return odometerDuration ?? 0;
        let timeNeededForOdometerToComplete = 0;
        subLevels.forEach((subLevel) => {
            const inStateName = subLevel.name + '_IN';
            const inAnimationName = this.winSpine.eventManager.getAnimationNameForGameEvent(inStateName);
            if (inAnimationName && inStateName !== EWinPresentationStates.WIN_PRESENTATION_IN_STATE) {
                timeNeededForOdometerToComplete += this.winSpine.getAnimationDuration(inAnimationName);
            }
            timeNeededForOdometerToComplete += subLevel.odometerDuration;
        });
        return timeNeededForOdometerToComplete;
    }
}
